import { memo, useMemo } from 'react';
import { IStatus, StatusPicker } from '@dfl/mui-react-common';
import { useSecurity } from '@dfl/react-security';
import { Box, CircularProgress } from '@mui/material';
import { IOrderStatus } from 'modules/sales/settings/order-status/interfaces';
import useSubOrderStatusUpdate from 'modules/sales/sub-orders/hooks/status/useSubOrderStatusUpdate';
import { useTranslateValue } from 'hooks/useTranslateValue';
import { IOrder } from '../../interfaces/IOrder';
import { useFindOrderStatusForUser } from '../../hooks/useOrderStatus';
import { ORDER_STATUS_TYPE_ENUM } from 'modules/sales/settings/order-status/constants';
import { isEmpty } from 'lodash';
import { ORDER_PERMISSIONS, SUBORDER_PERMISSIONS } from '../../constants/order-permissions';

type OrderStatusPickerProps = {
  value: IOrderStatus;
  record?: IOrder;
  button?: boolean;
  rowId: string;
  useHook?: any;
  readOnly?: boolean;
};

const OrderStatusPicker = ({
  value,
  button = false,
  rowId,
  useHook = useSubOrderStatusUpdate,
  readOnly,
}: OrderStatusPickerProps) => {
  const { hasPermission } = useSecurity();

  const canWrite = useMemo(
    () => hasPermission([ORDER_PERMISSIONS.ORDER_WRITE, SUBORDER_PERMISSIONS.SUBORDER_WRITE, ORDER_PERMISSIONS.ORDER_STATUS_VIEW]),
    [hasPermission],
  );

  const shouldFetchStatus = !readOnly && canWrite;

  /* update status */
  const { updateStatus: updateVisible, isLoading } = useHook(rowId);

  /* search order status */
  const {
    isLoading: isStatusOrderLoading,
    isError,
    orderStatus,
  } = useFindOrderStatusForUser({
    status: value,
    excludeTypes: [ORDER_STATUS_TYPE_ENUM.ERROR, ORDER_STATUS_TYPE_ENUM.CANCELED, ORDER_STATUS_TYPE_ENUM.COMPLETED],
    enabled: shouldFetchStatus,
  });

  const _value = useCheckLocale(value as IStatus);

  const isReadOnly = useMemo(
    () => readOnly || !canWrite || isEmpty(orderStatus),
    [readOnly, canWrite, orderStatus],
  );

  if (!value) return null;

  return (
    <Box
      sx={{
        '.MuiButton-root': {
          borderRadius: button ? '5px !important' : undefined,
          span: {
            whiteSpace: 'nowrap',
          },
        },
        button: {
          minWidth: 90,
          minHeight: button ? 36.5 : 'auto',
          alignItems: 'center',
          justifyContent: 'space-around',
          'MuiButton-endIcon': {
            marginLeft: '2px !important',
          },
        },
      }}
      className={isReadOnly ? 'pointer-events-none' : ''}
    >
      <div className='flex items-center justify-center relative gap-2'>
        {(isLoading) && (
          <CircularProgress color='secondary' className='absolute z-10 h-4 w-4' />
        )}
        <StatusPicker
          isError={isError}
          readOnly={isReadOnly}
          options={orderStatus as IStatus[]}
          name='status'
          size={'small'}
          isLoading={isLoading || isStatusOrderLoading}
          value={_value}
          onChange={(status: IStatus) => updateVisible(status?._id)}
        />
      </div>
    </Box>
  );
};

export default memo(OrderStatusPicker);

export const useCheckLocale = (value: IStatus): IStatus => {
  const { locale } = useTranslateValue();
  if (typeof value?.title !== 'string') {
    value = {
      ...value,
      title: value?.title?.[locale],
    };
  }
  return value;
};
